// The alarm screen (R1753; card 16 of mockup round 082; ui-map-interface.md
// §8 and §12): the signal rank, the siege row and the alarm list, as pure
// functions. The overlay owns the state and the presses; this module reads
// no field of it, so a test pins the order and the markup without a page.
//
// **The signal rank** is ONE order of every signal of the game screen, most
// urgent first. The readout line, the dock and the alarm list read it, so
// no surface keeps a second order:
//
//   1 the link: the maintenance window, or the lost link in its place
//   2 a siege warning
//   3 the timers: the faults first (war economy, the settlement), then the
//     schedules (seaquake stage 1, the founding truce)
//   4 the full stores
//   5 INBOX
//   6 CHAT
//   7 DEV, on an admin seat with a new build
//   8 the notices
//
// No signal takes gold (round 080), and each one reads by its shape: a
// fault is a moving red hatch, a schedule a light drain, FULL a lid, a
// count a light figure and DEV a light dot.

import { hhmm } from '../locale/clock';
import { leftSpan } from '../locale/span';
import { alarms as S, overlay as OS, siege as W } from '../gen/strings';
import type { SiegeInfo } from '../gen/proto/aquaphobia';
import { dur, num } from '../state';
import { esc } from './esc';
import { resIcon } from './resicon';
import type { TimerClaim } from './notices';
import { dockIcon, lightCount, lightDot } from './overlay/thumbdock';
import { clamp01 } from '../render/math';
import { lineIcon } from './lineicon';

/** The rank of each kind of signal (R1753). The notices hold rank 8 and
 *  stand in their own column; no alarm row names them. */
export const SIGNAL_RANK = {
  link: 1, siege: 2, timer: 3, full: 4, inbox: 5, chat: 6, dev: 7, notice: 8,
} as const;

/** The six clocks of the readout line. `window` is the siege window that
 *  concerns the reader, near or open (`ui/siegewindow.ts`); `seaview` is
 *  the countdown of the idle sea view (R2865). */
export type TimerId = 'maint' | 'link' | 'quake' | 'window' | 'truce' | 'seaview';

/** One running timer, with everything a surface draws of it. */
export interface RunningTimer {
  id: TimerId;
  claim: TimerClaim;
  /** The seaquake stage, 1 to 3, or 0 for the other three clocks. */
  stage: number;
  /** The share of the schedule still to run, 0 to 1. A fault draws no
   *  drain, so its share is never read. */
  frac: number;
  /** The siege window alone: the object's cell, for SHOW, and the line
   *  under the head of its alarm row, as markup. */
  cell?: number;
  what?: string;
}

/**
 * One siege warning: a faction other than the reader's pledged against an
 * object the reader's faction holds, and the window is open. A pledge is
 * taken only while the window runs (R787), so the warning counts down to
 * the window battle at the close of the window.
 */
export interface SiegeWarning {
  siteId: number;
  cell: number;
  /** The place of the object, as the WAR menu names it. */
  site: string;
  /** The faction that pledged against it, or '' when the frame names none. */
  attacker: string;
  /** World seconds to the window battle. */
  leftSec: number;
  /** The second of the world day at which the window closes. */
  closeSec: number;
  /** The share of the window still to run, 0 to 1. */
  frac: number;
}

/** The season reset, for the last row of the alarm list (R2941). */
export interface SeasonReset {
  season: number;
  /** World seconds to the reset. */
  leftSec: number;
  /** The timescale of the world: world seconds per wall second. */
  scale: number;
}

/** One store at its cap. */
export interface FullStore {
  name: string;
  have: number;
}

/** Everything that can signal, as the overlay reads it off the state. */
export interface SignalInput {
  timers: readonly RunningTimer[];
  sieges: readonly SiegeWarning[];
  full: readonly FullStore[];
  inbox: number;
  chat: number;
  /** An admin seat whose dev lane is behind its branch, else null. */
  dev: { behind: number; build: string } | null;
}

/** One running signal of the signal rank. */
export type Signal =
  | { kind: 'timer'; rank: number; timer: RunningTimer }
  | { kind: 'siege'; rank: number; siege: SiegeWarning }
  | { kind: 'full'; rank: number; stores: readonly FullStore[] }
  | { kind: 'inbox'; rank: number; count: number }
  | { kind: 'chat'; rank: number; count: number }
  | { kind: 'dev'; rank: number; behind: number; build: string };

/** The rank of one clock: the link and the maintenance window that takes
 *  its place are rank 1, the seaquake and the truce rank 3. */
function timerRank(id: TimerId): number {
  return id === 'maint' || id === 'link' ? SIGNAL_RANK.link : SIGNAL_RANK.timer;
}

/**
 * The clocks in the signal rank: rank 1 first, then the faults of rank 3,
 * then its schedules. Two clocks of one place keep the order they came in,
 * so the seaquake stays before the truce (R430).
 */
export function orderTimers(timers: readonly RunningTimer[]): RunningTimer[] {
  const place = (t: RunningTimer) => timerRank(t.id) * 2 + (t.claim.fault ? 0 : 1);
  return timers.map((t, i) => ({ t, i }))
    .sort((a, b) => place(a.t) - place(b.t) || a.i - b.i)
    .map((x) => x.t);
}

/** Every running signal, most urgent first (R1753). */
export function rankSignals(v: SignalInput): Signal[] {
  const timers = orderTimers(v.timers);
  const out: Signal[] = [];
  for (const t of timers) {
    if (timerRank(t.id) === SIGNAL_RANK.link) out.push({ kind: 'timer', rank: SIGNAL_RANK.link, timer: t });
  }
  for (const s of v.sieges) out.push({ kind: 'siege', rank: SIGNAL_RANK.siege, siege: s });
  for (const t of timers) {
    if (timerRank(t.id) === SIGNAL_RANK.timer) out.push({ kind: 'timer', rank: SIGNAL_RANK.timer, timer: t });
  }
  if (v.full.length > 0) out.push({ kind: 'full', rank: SIGNAL_RANK.full, stores: v.full });
  if (v.inbox > 0) out.push({ kind: 'inbox', rank: SIGNAL_RANK.inbox, count: v.inbox });
  if (v.chat > 0) out.push({ kind: 'chat', rank: SIGNAL_RANK.chat, count: v.chat });
  if (v.dev && v.dev.behind > 0) {
    out.push({ kind: 'dev', rank: SIGNAL_RANK.dev, behind: v.dev.behind, build: v.dev.build });
  }
  return out;
}

/**
 * The rows of the alarm list: every running signal with a press of its
 * own. CHAT is not a row: its lines wait in the chat, and the VIEW button
 * counts them (R1747).
 */
export function alarmRows(signals: readonly Signal[]): Signal[] {
  return signals.filter((s) => s.kind !== 'chat');
}

/** What the time cell of the readout line shows. */
export interface TimeCellView {
  /** The most urgent clock, which stands under the world clock. */
  first: RunningTimer | null;
  /** `+N`: the other signals of ranks 1 to 3, the siege warnings included. */
  more: number;
  /** A press opens the alarm list: two rows or more run. */
  list: boolean;
}

/**
 * The time cell (R1749, R1753). The siege warning has its own row in the
 * dock, so it never stands under the clock, but `+N` counts it: the cell
 * counts what runs behind the clock it names.
 */
export function timeCell(signals: readonly Signal[]): TimeCellView {
  const urgent = signals.filter((s) => s.rank <= SIGNAL_RANK.timer);
  const first = urgent.find((s) => s.kind === 'timer');
  return {
    first: first?.kind === 'timer' ? first.timer : null,
    more: first ? urgent.length - 1 : 0,
    list: alarmRows(signals).length > 1,
  };
}

/**
 * True while an alarm runs: a siege warning or a fault (the lost link, the
 * war economy, the settlement). RESTART then takes the dark ink, so the
 * screen holds one solid light press at most, for the first action (card
 * 16). A schedule, a full store and a count are no alarm in this sense.
 */
export function alarmRuns(signals: readonly Signal[]): boolean {
  return signals.some((s) => s.kind === 'siege' || (s.kind === 'timer' && s.timer.claim.fault));
}

/** What `siegeWarnings` must be told about the reader and the world. */
export interface SiegeWorld {
  /** The reader's faction; 0 before the hello. */
  faction: number;
  /** The length of a window, in world seconds (`rules.objWindowSec`). */
  span: number;
  /** World seconds to the next window of an hour; negative while open
   *  (`GameState.windowInSec`). */
  windowIn: (hour: number) => number;
  /** The place of an object, as the WAR menu names it. */
  site: (g: SiegeInfo) => string;
  /** The name of a faction. */
  attacker: (faction: number) => string;
}

/**
 * The siege warnings of the reader, the window battle that comes first
 * first (R1753). The Sieges frame states who pledged where (R794), and the
 * server sends a new frame on every pledge and every window battle. The
 * window must be open as well: a frame the client did not get must not
 * hold a warning past the battle it warned of.
 */
export function siegeWarnings(list: readonly SiegeInfo[], w: SiegeWorld): SiegeWarning[] {
  if (w.faction === 0 || !(w.span > 0)) return [];
  const out: SiegeWarning[] = [];
  for (const g of list) {
    if (g.owner !== w.faction || !(g.enemyPledged || g.contested)) continue;
    const inSec = w.windowIn(g.windowHour);
    if (!(inSec < 0)) continue;
    const left = Math.max(0, w.span + inSec);
    out.push({
      siteId: g.siteId,
      cell: g.cell,
      site: w.site(g),
      attacker: g.attacker ? w.attacker(g.attacker) : '',
      leftSec: left,
      closeSec: g.windowHour * 3600 + w.span,
      frac: Math.min(1, left / w.span),
    });
  }
  return out.sort((a, b) => a.leftSec - b.leftSec || a.siteId - b.siteId);
}

/** The two line icons of the alarm screen, in the stroke of the dock. */
const ICON = {
  // A flag on its pole: the siege warning.
  flag: '<path d="M6 21V3.5"/><path d="M6 4.5h11.5l-2.6 4.25 2.6 4.25H6"/>',
  // A ring and its sights: GO TO.
  target: '<circle cx="12" cy="12" r="6.5"/><circle cx="12" cy="12" r="2"/>'
    + '<path d="M12 2.5v3.5M12 18v3.5M2.5 12H6M18 12h3.5"/>',
  // A cross: close the list.
  close: '<path d="M6.5 6.5l11 11M17.5 6.5l-11 11"/>',
} as const;

function icon(name: keyof typeof ICON, px: number): string {
  return lineIcon('dic', px, ICON[name]);
}

/** A light drain along the foot of a row: the share still to run. */
function drain(frac: number): string {
  const pct = Math.round(clamp01(frac) * 100);
  return `<i class="drn"><b style="width:${pct}%"></b></i>`;
}

/** The press GO TO. `light` makes it the solid light press of the screen. */
function goTo(w: SiegeWarning, light: boolean, cls: string): string {
  return `<button class="${cls}${light ? ' light' : ''}" data-act="siegego" data-id="${w.siteId}"`
    + ` data-n="${w.cell}" title="${esc(S.go_to_title({ site: w.site }))}">`
    + `${icon('target', 14)}<span>${S.go_to}</span></button>`;
}

/**
 * The siege row (R1753): the flag, SIEGE WARNING over the place, the time
 * of the window battle over its countdown (a span with units, R2756), and
 * GO TO, the one solid light press of the screen. A light drain runs along its foot over the window.
 * A press on the row outside GO TO opens the alarm list.
 */
export function siegeRowHtml(w: SiegeWarning): string {
  const at = hhmm(w.closeSec);
  return `<button class="sg-open" data-act="alarms" aria-haspopup="true"`
    + ` title="${esc(S.siege_title({ site: w.site, at }))}">`
    + `<span class="sg-i">${icon('flag', 16)}</span>`
    + `<span class="sg-t"><i>${S.siege_head}</i><b>${esc(w.site)}</b></span>`
    + `<span class="sg-c"><i>${S.siege_battle({ at })}</i><b>${leftSpan(w.leftSec)}</b></span></button>`
    + goTo(w, true, 'sg-go')
    + drain(w.frac);
}

/** One row of the alarm list. */
function row(o: {
  n: number | ''; cls: string; mark: string; head: string; what?: string; value?: string;
  press: string; foot?: string;
}): string {
  return `<div class="alr ${o.cls}"><span class="aln">${o.n}</span><span class="alm">${o.mark}</span>`
    + `<span class="alt"><b>${o.head}</b>${o.what ? `<i>${o.what}</i>` : ''}</span>`
    + (o.value ? `<span class="alv">${o.value}</span>` : '')
    + o.press + (o.foot ?? '') + '</div>';
}

/** A dark press of a row. */
function press(act: string, label: string, title: string): string {
  return `<button class="alb" data-act="${act}" title="${esc(title)}">${label}</button>`;
}

/** The hatch of a fault, the mark of the drain line's moving red hatch. */
const HATCH = '<i class="hatch"></i>';

/** The line under the head of each schedule that the READ press closes. */
const SCHED_WHAT: Readonly<Record<'maint' | 'truce' | 'seaview', string>> = {
  maint: S.maint_what, truce: S.truce_what, seaview: S.seaview_what,
};

/** The line under the head of each seaquake stage. */
function quakeWhat(stage: number): string {
  return stage >= 3 ? S.quake_what_3 : stage === 2 ? S.quake_what_2 : S.quake_what_1;
}

/** The row of one clock. */
function timerRow(n: number, t: RunningTimer): string {
  const c = t.claim;
  const fault = c.fault;
  const mark = fault ? HATCH : `<span class="glyph">${c.glyph}</span>`;
  const foot = fault ? '' : drain(t.frac);
  switch (t.id) {
    case 'link':
      return row({
        n, cls: 'fault', mark, head: c.label, what: S.link_what, value: c.figure,
        press: press('alarmcheck', S.act_check, S.act_check_title),
      });
    case 'window':
      return row({
        n, cls: 'sched', mark, head: c.label, what: t.what ?? '', value: c.figure, foot,
        press: `<button class="alb" data-act="focuscell" data-n="${t.cell ?? 0}" title="${esc(W.show_title)}">${W.show}</button>`,
      });
  }
  // The seaquake is a fault from stage 2, and its claim's label is the
  // stage's one name (R2090). The other three are schedules.
  const quake = t.id === 'quake';
  return row({
    n, cls: quake && fault ? 'fault' : 'sched', mark, head: c.label,
    what: t.id === 'quake' ? quakeWhat(t.stage) : SCHED_WHAT[t.id], value: c.figure,
    press: press('alarmread', S.act_read, S.act_read_title), foot,
  });
}

/**
 * The last row of the alarm list (R2941): the season reset on one line,
 * the head and its figure, the world time and, while the timescale is not
 * 1, the real time. It is no alarm, so it takes no position, no count and
 * no press. While two alarms or more run, a press on the time cell opens
 * this list in place of the clock's card, and a telephone has no hover,
 * so this row states the reset there.
 */
function resetRow(r: SeasonReset): string {
  const world = leftSpan(r.leftSec);
  return row({
    n: '', cls: 'reset', mark: `<span class="glyph">${resIcon('clock')}</span>`,
    head: S.reset_head({ season: r.season }), press: '',
    value: r.scale !== 1 ? S.reset_real({ world, real: dur(r.leftSec / r.scale) }) : world,
  });
}

/**
 * The alarm list (R1753): every running alarm in the signal rank, most
 * urgent first, each row with its one press. The first siege warning
 * carries the one solid light press; every other press wears the dark ink.
 * The season reset stands under the alarms (R2941).
 */
export function alarmListHtml(rows: readonly Signal[], reset: SeasonReset | null = null): string {
  let lit = false;
  const body = rows.map((sig, i) => {
    const n = i + 1;
    switch (sig.kind) {
      case 'timer': return timerRow(n, sig.timer);
      case 'siege': {
        const w = sig.siege;
        const light = !lit;
        lit = true;
        return row({
          n, cls: 'sg', mark: icon('flag', 15), head: S.siege_head,
          what: w.attacker === '' ? esc(w.site) : S.siege_what({ site: esc(w.site), attacker: esc(w.attacker) }),
          value: leftSpan(w.leftSec), press: goTo(w, light, 'alb'), foot: drain(w.frac),
        });
      }
      case 'full': {
        const one = sig.stores.length === 1;
        const stores = sig.stores.map((f) => S.full_store({ name: esc(f.name), have: num(f.have) })).join(' · ');
        return row({
          n, cls: 'full', mark: '<span class="lid"><i></i><b></b></span>',
          head: one ? S.full_one({ name: esc(sig.stores[0].name) }) : S.full_many({ n: sig.stores.length }),
          what: S.full_what({ stores }),
          press: press('alarmbuild', S.act_build, S.act_build_title),
        });
      }
      case 'inbox':
        return row({
          n, cls: 'count', mark: lightCount(sig.count), head: S.inbox_head, what: S.inbox_what({ n: sig.count }),
          press: press('alarminbox', S.act_open, S.act_open_title),
        });
      case 'dev':
        return row({
          n, cls: 'dev', mark: lightDot(true), head: S.dev_head,
          what: `${OS.head_commits({ n: sig.behind })} · ${esc(OS.head_build({ build: sig.build }))}`,
          press: `<button class="alb" data-act="alarmrestart">${dockIcon('restart', 13)}${S.act_restart}</button>`,
        });
      case 'chat':
        return '';
    }
  }).join('') + (reset && reset.leftSec > 0 ? resetRow(reset) : '');
  return `<div class="alh"><b>${S.list_head}</b><i>${S.list_count({ n: rows.length })}</i>`
    + `<button class="alx" data-act="alarmsclose" title="${esc(S.list_close)}" aria-label="${esc(S.list_close)}">`
    + `${icon('close', 14)}</button></div>`
    + `<div class="alrows">${body}</div>`;
}
